// hooks/useNotificationsDebug.js
import { useEffect, useState } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

export default function useNotificationsDebug({ limitResults = 100 } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debug, setDebug] = useState({}); // expose debug info

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    let unsub = null;
    let mounted = true;

    const start = async (user) => {
      if (!mounted) return;
      if (!user) {
        console.warn('useNotificationsDebug: no auth user');
        setNotifications([]);
        setLoading(false);
        setDebug((d) => ({ ...d, authUser: null }));
        return;
      }

      // console.log('useNotificationsDebug: auth user uid=', user.uid);
      setDebug((d) => ({ ...d, authUser: user.uid }));

      try {
        const q = query(
          collection(db, 'notifications'),
          where('to', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(limitResults)
        );

        // console.log('useNotificationsDebug: query created', q);

        // one-time getDocs() to see what the server returns
        try {
          const snap = await getDocs(q);
          // console.log('useNotificationsDebug: getDocs result size=', snap.size);

          // ✅ Filter out type === "message"
          const docsOnce = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((n) => n.type !== 'message');

          // console.log('useNotificationsDebug: getDocs docs (filtered)=', docsOnce);
          setDebug((d) => ({ ...d, getDocs: docsOnce }));
        } catch (getErr) {
          console.error('useNotificationsDebug: getDocs error', getErr);
          setDebug((d) => ({ ...d, getDocsError: String(getErr) }));
        }

        // realtime listener
        unsub = onSnapshot(
          q,
          (snap) => {
            // console.log('useNotificationsDebug: onSnapshot snapshot size=', snap.size);

            const docs = snap.docs
              .map((d) => {
                const data = d.data();
                const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt;
                return { id: d.id, createdAt, ...data };
              })
              // ✅ Filter out message-type notifications
              .filter((n) => n.type !== 'message');

            // console.log('useNotificationsDebug: onSnapshot docs (filtered)=', docs);
            setNotifications(docs);
            setLoading(false);
            setDebug((d) => ({ ...d, lastSnapshotSize: docs.length }));
          },
          (err) => {
            console.error('useNotificationsDebug: onSnapshot error', err);
            setDebug((d) => ({ ...d, onSnapshotError: String(err) }));
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('useNotificationsDebug: build/query error', err);
        setDebug((d) => ({ ...d, buildError: String(err) }));
        setLoading(false);
      }
    };

    const unsubAuth = onAuthStateChanged(auth, (user) => start(user));

    return () => {
      mounted = false;
      unsubAuth();
      if (unsub) unsub();
    };
  }, [limitResults]);

  return { notifications, loading, debug };
}
